import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Address.module.css";
import { billingInfoActions } from "@_actions/billing_info.actions";
import { isValidEmail } from "@_utils/helpers";
import {
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

function Address(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const caseInfo = useSelector((state) => state.caseDetailsReducer);
  const chargeDetails = useSelector((state) => state.chargeReducer);
  const billingReducer = useSelector((state) => state.billingInfoReducer);
  const [errorMessage, setErrorMessage] = useState(null);
  const [proceed_disabled, setProceed_disabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [showNameWarning, setShowNameWarning] = useState(false);

  const hasFieldValue = (value) => {
    if (value === null || value === undefined) {
      return false;
    }

    return String(value).trim().length > 0;
  };

  const [billingAddress, setBillingAddressInternal] = useState({
    email: "",
    name: "",
    vat_id: "",
    custom_fields: [],
    address_line_1: "",
    address_line_2: "",
    postal_code: "",
    city: "",
    country: "",
  });
  let charge = chargeDetails.charge;
  const customFields = billingAddress.custom_fields || [];
  const requiredCustomFieldsValid = customFields
    .filter((field) => field?.required)
    .every((field) => hasFieldValue(field?.value));
  const emailValid = isValidEmail(billingAddress.email);
  const showEmailError = hasFieldValue(billingAddress.email) && !emailValid;
  const requiredBillingFieldsValid =
    emailValid &&
    hasFieldValue(billingAddress.name) &&
    hasFieldValue(billingAddress.address_line_1) &&
    hasFieldValue(billingAddress.postal_code) &&
    hasFieldValue(billingAddress.city) &&
    hasFieldValue(billingAddress.country);
  const vatFieldValid =
    !caseInfo.caseDetails.vat_id_required ||
    hasFieldValue(billingAddress.vat_id);

  useEffect(() => {
    if (
      billingAddress &&
      vatFieldValid &&
      requiredBillingFieldsValid &&
      requiredCustomFieldsValid
    ) {
      setProceed_disabled(false);
    } else setProceed_disabled(true);
  }, [
    billingAddress,
    caseInfo.caseDetails.vat_id_required,
    requiredBillingFieldsValid,
    requiredCustomFieldsValid,
    vatFieldValid,
  ]);

  function setBillingAddress(value) {
    setShowNameWarning(false);
    setBillingAddressInternal(value);
  }

  useEffect(() => {
    if (!charge) {
      return <Redirect from="*" to="/caseinfo" />;
    }
    if (caseInfo && caseInfo.caseDetails.billing_address) {
      setBillingAddress({
        email: caseInfo.caseDetails.billing_address.email || "",
        name: caseInfo.caseDetails.billing_address.name || "",
        vat_id: caseInfo.caseDetails.billing_address.vat_id || "",
        custom_fields: Array.isArray(
          caseInfo.caseDetails.billing_address.custom_fields,
        )
          ? caseInfo.caseDetails.billing_address.custom_fields
          : [],
        address_line_1:
          caseInfo.caseDetails.billing_address.address_line_1 || "",
        address_line_2:
          caseInfo.caseDetails.billing_address.address_line_2 || "",
        postal_code: caseInfo.caseDetails.billing_address.postal_code || "",
        city: caseInfo.caseDetails.billing_address.city || "",
        country: caseInfo.caseDetails.country || "",
      });
    } else if (caseInfo && caseInfo.caseDetails.opponent_contact_details) {
      setBillingAddress({
        email: caseInfo.caseDetails.opponent_contact_details.email || "",
        name: caseInfo.caseDetails.opponent_contact_details.company
          ? caseInfo.caseDetails.opponent_contact_details.company
          : caseInfo.caseDetails.opponent_contact_details.name || "",
        vat_id: "",
        custom_fields: Array.isArray(
          caseInfo.caseDetails.opponent_contact_details.custom_fields,
        )
          ? caseInfo.caseDetails.opponent_contact_details.custom_fields
          : [],
        address_line_1:
          caseInfo.caseDetails.opponent_contact_details.address_line_1 || "",
        address_line_2:
          caseInfo.caseDetails.opponent_contact_details.address_line_2 || "",
        postal_code:
          caseInfo.caseDetails.opponent_contact_details.postal_code || "",
        city: caseInfo.caseDetails.opponent_contact_details.city || "",
        country: caseInfo.caseDetails.country || "",
      });
    }
  }, [caseInfo, caseInfo.caseDetails.billing_address, charge]);

  const handleNext = () => {
    setIsClicked(true);
    // check if eather name or company name is set
    if (!hasFieldValue(billingAddress.name)) {
      setShowNameWarning(true);
      return;
    }
    if (
      !(
        vatFieldValid &&
        requiredBillingFieldsValid &&
        requiredCustomFieldsValid
      )
    ) {
      return;
    }
    // show loading indicator
    setIsLoading(true);
    //call api
    dispatch(
      billingInfoActions.billingInfo({
        ...billingAddress,
        email: billingAddress.email.trim(),
      }),
    );
    // dispatch(caseDetailsActions.caseDetails());
  };

  useEffect(() => {
    if (billingReducer.message && billingReducer.message.trim().length > 0) {
      setIsLoading(false);
      setErrorMessage(t("_submission_failed"));
      dispatch(billingInfoActions.reset());
    } else if (billingReducer.billing_address_saved === true) {
      props.next();
    }
    // eslint-disable-next-line
  }, [billingReducer, t]);

  const handleClose = () => {
    setIsLoading(false);
    setIsClicked(false);
    props.onClose();
  };

  const updateCustomFieldValue = (index, value) => {
    if (index < 0 || index >= customFields.length) {
      return;
    }

    const updatedCustomFields = [...customFields];
    const currentField = updatedCustomFields[index] || {};

    updatedCustomFields[index] = {
      ...currentField,
      value,
    };

    setBillingAddress({
      ...billingAddress,
      custom_fields: updatedCustomFields,
    });
  };

  const renderCustomField = (field, index) => {
    if (!field) {
      return null;
    }

    return (
      <FormControl className={styles.half_form} key={`custom-field-${index}`}>
        <FormLabel className={styles.text_label}>
          {field?.label || "Custom Field"}
          {field?.required ? "*" : ""}
        </FormLabel>
        <TextField
          value={field?.value || ""}
          size="small"
          onChange={(e) => {
            updateCustomFieldValue(index, e.target.value);
          }}
          variant="outlined"
          error={isClicked && field?.required && !hasFieldValue(field?.value)}
        ></TextField>
      </FormControl>
    );
  };

  const renderCustomFieldsRows = (startFrom = 0) => {
    if (!customFields.length || startFrom >= customFields.length) {
      return null;
    }

    const rowStartIndexes = customFields
      .map((_, index) => index)
      .filter((index) => index >= startFrom)
      .filter((index) => (index - startFrom) % 2 === 0);

    return rowStartIndexes.map((startIndex) => (
      <div
        className={styles.address_row}
        key={`custom-fields-row-${startIndex}`}
      >
        {renderCustomField(customFields[startIndex], startIndex)}
        {renderCustomField(customFields[startIndex + 1], startIndex + 1)}
      </div>
    ));
  };

  const normalView = (
    <>
      <div className={`${styles.address_row} ${styles.address_row_start}`}>
        <FormControl className={styles.half_form}>
          <FormLabel className={styles.text_label}>
            {t("_company")}/{t("_name") + " *"}
          </FormLabel>
          <TextField
            variant="outlined"
            required
            size="small"
            value={billingAddress.name}
            type="text"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                name: e.target.value,
              });
            }}
          ></TextField>
        </FormControl>
        <FormControl
          className={`${styles.half_form} ${styles.field_with_message}`}
        >
          <FormLabel className={styles.text_label}>
            {t("_email") + " *"}
          </FormLabel>
          <TextField
            variant="outlined"
            type="email"
            required
            size="small"
            value={billingAddress.email}
            error={showEmailError}
            helperText={showEmailError ? t("_invalid_email") : ""}
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                email: e.target.value,
              });
            }}
          ></TextField>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <FormControl fullWidth>
          <FormLabel className={styles.text_label}>
            {t("_address_line") + " 1 *"}
          </FormLabel>
          <TextField
            variant="outlined"
            id="street"
            value={billingAddress.address_line_1}
            type="text"
            size="small"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                address_line_1: e.target.value,
              });
            }}
            fullWidth
          ></TextField>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <FormControl fullWidth>
          <FormLabel className={styles.text_label}>
            {t("_address_line") + " 2"}
          </FormLabel>
          <TextField
            id="street"
            variant="outlined"
            value={billingAddress.address_line_2}
            type="text"
            size="small"
            fullWidth
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                address_line_2: e.target.value,
              });
            }}
          ></TextField>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <div className={styles.half_form}>
          <FormControl fullWidth>
            <FormLabel className={styles.text_label}>
              {t("_city") + " *"}
            </FormLabel>
            <TextField
              required
              size="small"
              value={billingAddress.city}
              type="text"
              onChange={(e) => {
                setBillingAddress({
                  ...billingAddress,
                  city: e.target.value,
                });
              }}
              variant="outlined"
            ></TextField>
          </FormControl>
        </div>
        <div className={`${styles.half_form} ${styles.address_container} `}>
          <FormControl style={{ width: "45%" }}>
            <FormLabel className={styles.text_label}>
              {t("_postal_code") + " *"}
            </FormLabel>
            <TextField
              required
              size="small"
              value={billingAddress.postal_code}
              type="text"
              onChange={(e) => {
                setBillingAddress({
                  ...billingAddress,
                  postal_code: e.target.value,
                });
              }}
              variant="outlined"
            ></TextField>
          </FormControl>
          <FormControl style={{ width: "25%" }}>
            <FormLabel>{t("_country")}</FormLabel>
            <p className={styles.country_text}>{billingAddress.country}</p>
          </FormControl>
        </div>
      </div>
      <div className={styles.address_row}>
        <FormControl className={styles.half_form}>
          <FormLabel className={styles.text_label}>
            {" "}
            {t("_vat_id")} {caseInfo.caseDetails.vat_id_required ? " *" : ""}
          </FormLabel>
          <TextField
            value={billingAddress.vat_id}
            size="small"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                vat_id: e.target.value,
              });
            }}
            variant="outlined"
            error={
              isClicked &&
              caseInfo.caseDetails.vat_id_required &&
              !hasFieldValue(billingAddress.vat_id)
            }
          ></TextField>
        </FormControl>
        {renderCustomField(customFields[0], 0)}
      </div>
      {renderCustomFieldsRows(1)}
    </>
  );

  const mobileView = (
    <>
      <div className={styles.address_row}>
        <FormControl fullWidth>
          <FormLabel className={styles.text_label}>
            {t("_company")}/{t("_name") + " *"}
          </FormLabel>
          <TextField
            variant="outlined"
            required
            size="small"
            value={billingAddress.name}
            type="text"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                name: e.target.value,
              });
            }}
          ></TextField>
        </FormControl>
      </div>
      <div className={`${styles.address_row} ${styles.address_row_start}`}>
        <FormControl fullWidth className={styles.field_with_message}>
          <FormLabel className={styles.text_label}>
            {t("_email") + " *"}
          </FormLabel>
          <TextField
            variant="outlined"
            type="email"
            required
            size="small"
            value={billingAddress.email}
            error={showEmailError}
            helperText={showEmailError ? t("_invalid_email") : ""}
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                email: e.target.value,
              });
            }}
          ></TextField>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <FormControl fullWidth>
          <FormLabel className={styles.text_label}>
            {t("_address_line") + " 1 *"}
          </FormLabel>
          <TextField
            variant="outlined"
            id="street"
            value={billingAddress.address_line_1}
            type="text"
            size="small"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                address_line_1: e.target.value,
              });
            }}
            fullWidth
          ></TextField>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <FormControl fullWidth>
          <FormLabel className={styles.text_label}>
            {t("_address_line") + " 2"}
          </FormLabel>
          <TextField
            id="street"
            variant="outlined"
            value={billingAddress.address_line_2}
            type="text"
            size="small"
            fullWidth
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                address_line_2: e.target.value,
              });
            }}
          ></TextField>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <FormControl className={styles.half_form}>
          <FormLabel className={styles.text_label}>
            {t("_city") + " *"}
          </FormLabel>
          <TextField
            required
            size="small"
            value={billingAddress.city}
            type="text"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                city: e.target.value,
              });
            }}
            variant="outlined"
          ></TextField>
        </FormControl>
        <FormControl className={styles.half_form}>
          <FormLabel className={styles.text_label}>
            {t("_postal_code") + " *"}
          </FormLabel>
          <TextField
            required
            size="small"
            value={billingAddress.postal_code}
            type="text"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                postal_code: e.target.value,
              });
            }}
            variant="outlined"
          ></TextField>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <FormControl fullWidth>
          <FormLabel className={styles.text_label}>{t("_country")}</FormLabel>
          <p className={styles.country_text}> {billingAddress.country}</p>
        </FormControl>
      </div>
      <div className={styles.address_row}>
        <FormControl className={styles.half_form}>
          <FormLabel className={styles.text_label}>
            {" "}
            {t("_vat_id")} {caseInfo.caseDetails.vat_id_required ? " *" : ""}
          </FormLabel>
          <TextField
            value={billingAddress.vat_id}
            size="small"
            onChange={(e) => {
              setBillingAddress({
                ...billingAddress,
                vat_id: e.target.value,
              });
            }}
            variant="outlined"
            error={
              isClicked &&
              caseInfo.caseDetails.vat_id_required &&
              !hasFieldValue(billingAddress.vat_id)
            }
          ></TextField>
        </FormControl>
        {renderCustomField(customFields[0], 0)}
      </div>
      {renderCustomFieldsRows(1)}
    </>
  );
  return (
    <div className={styles.address_modal}>
      <div className={styles.header_container}>
        <h2 className={styles.address_header}>{t("_billing_information")}</h2>
        <CloseIcon onClick={handleClose} style={{ marginTop: 22 }}></CloseIcon>
      </div>
      <div className={styles.billing_container}>
        <div className={styles.normal_view}> {normalView} </div>
        <div className={styles.mobile_view}> {mobileView} </div>
        <div>
          <div className={styles.actionsContainer}>
            {isLoading && !props.hideLoading ? (
              <CircularProgress />
            ) : (
              <>
                {errorMessage ? (
                  <Typography style={{ color: "red" }}>
                    {errorMessage}
                  </Typography>
                ) : (
                  <>
                    <div className={styles.nameWarningContainer}>
                      {showNameWarning ? (
                        t("_name_or_company_warning")
                      ) : (
                        <>&nbsp;</>
                      )}
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      className={styles.button}
                      disabled={proceed_disabled}
                    >
                      {props.button || "next"}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Address;
